import { SHA256_DIGEST_PATTERN, base64UrlEncode, kernelCanonicalJson, sha256DigestSchema } from "./kernel-encoding.js";
import { KERNEL_KID_PATTERN, LOWER_UUID_PATTERN, base64UrlBytesOrNull, canonicalByteLength, importEs256VerifyKey, isStrictlyAscending, verifyEs256 } from "./kernel-internal.js";
import { LIBRARY_SLUG_PATTERN, libraryRightListSchema } from "../library/library-rights.js";
import { libraryInstanceSlug } from "../library/library-instance-slug.js";
import { isLibraryEventTopic } from "../library/library-topics.js";
import { kernelRightListSchema } from "./kernel-rights.js";
import { kernelImageListSchema, kernelUiFileListSchema } from "./release-statement.js";
import "../library/library-use.js";
import { z } from "zod";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/kernel/rights-certificate.ts
/**
* The rights certificate: the CP-signed list of the kernel rights that one app version holds
* (kernel apps, WS-1a, decision 13, I-5, R-005, R-020, R-042, R-062).
*
* A rights certificate is a compact JWS that the CP signs with its own Cloud KMS key (WS-8b). It
* names one app version, the release statement it covers (`stmt`), each image and UI file of that
* version and the granted kernel rights (`krt`). The desired state carries it to each DP (WS-2a).
* The operator program (WS-22), core-api (WS-21) and the asset proxy (WS-18) verify it against the
* pin of the DP, the trust anchor that WS-4 writes and WS-5 mounts.
*
* Browser-safe: no Node builtin and no Node global. Signature checks use the WebCrypto global
* `crypto.subtle`. No exported signature names a DOM type.
*/
/** The JWS `typ` header of every rights certificate. It keeps a CP access token out of this verifier. */
const RIGHTS_CERTIFICATE_TYP = "zion-rights-certificate+jwt";
/** The `aud` claim of every rights certificate. */
const RIGHTS_CERTIFICATE_AUDIENCE = "zion-dp-kernel";
/** The largest certificate token, in characters: the WS-2a wire cap (R-005). */
const RIGHTS_CERTIFICATE_MAX_BYTES = 65536;
/**
* The longest certificate lifetime, `exp - iat`, in seconds: 72 hours (R-062). The CP signs a new
* certificate every 24 hours (WS-8b), so a revoked right leaves a DP within the lifetime.
*/
const RIGHTS_CERTIFICATE_MAX_LIFETIME_SECONDS = 259200;
/** The clock skew that the time checks of the verifier allow, in seconds. */
const RIGHTS_CERTIFICATE_CLOCK_SKEW_SECONDS = 60;
/** A certificate key id: 1 to 128 base64url characters. The CP writes the RFC 7638 thumbprint (R-006). */
const RIGHTS_CERTIFICATE_KID_PATTERN = KERNEL_KID_PATTERN;
/**
* The file name of a stored certificate: `<appSlug>.jws` (R-020). The asset proxy (WS-18) and the
* operator program (WS-22) read and write the key with this pattern.
*/
const RIGHTS_CERTIFICATE_FILE_PATTERN = /^[a-z][a-z0-9-]{0,29}\.jws$/;
/** The most rights certificates one machine principal enrol carries (R-005, R-044). */
const KERNEL_ENROL_CERTIFICATES_MAX = 8;
/** The most keys a trust anchor or a rights JWKS holds. */
const RIGHTS_KEYS_MAX = 8;
/** The length of a signing input that still fits the wire cap: the cap less a full stop and 86 signature characters. */
const SIGNING_INPUT_MAX_CHARS = 65449;
/** An `https:` URL of printable ASCII with no `"` and no `\`. The schemas apply `.max(256)` before it. */
const ISSUER_PATTERN = /^https:\/\/[!#$%&'()*+,\-./0-9:;<=>?@A-Z[\]^_`a-z{|}~]+$/;
const APP_SLUG_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$/;
const APP_VERSION_PATTERN = /^\d+\.\d+\.\d+/;
const COORDINATE_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** The most UTF-8 bytes the canonical JSON of `{ lrt, lev }` may take in one certificate. */
const RIGHTS_CERTIFICATE_LIBRARY_CLAIMS_MAX_BYTES = 16384;
/** The zod form of {@link RightsCertificateHeader}. Strict. */
const rightsCertificateHeaderSchema = z.strictObject({
	alg: z.literal("ES256"),
	typ: z.literal(RIGHTS_CERTIFICATE_TYP),
	kid: z.string().max(128).regex(RIGHTS_CERTIFICATE_KID_PATTERN)
});
const issuerSchema = z.string().max(256).regex(ISSUER_PATTERN);
const epochSecondsSchema = z.number().int().nonnegative();
const LOWER_UUID_SCHEMA = z.string().max(36).regex(LOWER_UUID_PATTERN);
const LIBRARY_SLUG_SCHEMA = z.string().max(30).regex(LIBRARY_SLUG_PATTERN);
/** A topic list of a `lev` entry: library event topics, unique and in code-point order. */
const levTopicListSchema = z.array(z.string().max(63).refine(isLibraryEventTopic, { message: "not a library event topic" })).max(32).refine(isStrictlyAscending, { message: "topics must be unique and in code-point order" });
/** The zod form of one {@link LibraryEventClaim}. Strict. */
function libraryEventClaimSchema() {
	return z.strictObject({
		id: LOWER_UUID_SCHEMA,
		slug: LIBRARY_SLUG_SCHEMA,
		inbound: levTopicListSchema,
		outbound: levTopicListSchema,
		consumer: z.strictObject({
			id: LOWER_UUID_SCHEMA,
			slug: z.string().max(30).regex(APP_SLUG_PATTERN)
		}).optional()
	});
}
/** Every claim rule except `aud`. The verifier reads `aud` as text first, so a wrong audience has its own refusal. */
const claimFields = {
	iss: issuerSchema,
	sub: z.string().max(36).regex(LOWER_UUID_PATTERN),
	jti: z.string().max(36).regex(LOWER_UUID_PATTERN),
	iat: epochSecondsSchema,
	nbf: epochSecondsSchema,
	exp: epochSecondsSchema,
	appSlug: z.string().max(30).regex(APP_SLUG_PATTERN),
	version: z.string().max(64).regex(APP_VERSION_PATTERN),
	bundleDigest: sha256DigestSchema,
	images: kernelImageListSchema,
	uiFiles: kernelUiFileListSchema,
	krt: kernelRightListSchema,
	lrt: libraryRightListSchema.optional(),
	lev: z.array(libraryEventClaimSchema()).min(1).max(17).refine((entries) => isStrictlyAscending(entries.map((entry) => entry.slug)), { message: "lev entries must be in slug order with no duplicate slug" }).optional(),
	csk: z.strictObject({
		kid: z.string().max(128).regex(RIGHTS_CERTIFICATE_KID_PATTERN),
		jkt: z.string().max(43).regex(COORDINATE_PATTERN)
	}),
	stmt: sha256DigestSchema
};
/** The instance slug of the three values, or `null` when `libraryInstanceSlug` refuses one of them. */
function instanceSlugOrNull(librarySlug, libraryId, consumerId) {
	try {
		return libraryInstanceSlug(librarySlug, libraryId, consumerId);
	} catch {
		return null;
	}
}
/** The cross-claim rules of the library claims, shared by the strict schema and the verifier's schema. */
function checkLibraryClaims(claims, ctx) {
	const lev = claims.lev ?? [];
	const instanceIndexes = lev.flatMap((entry, index) => entry.consumer === void 0 ? [] : [index]);
	if (claims.krt.length === 0 && claims.lrt === void 0 && lev.length === 0) {
		ctx.addIssue({
			code: "custom",
			path: ["krt"],
			message: "krt or lrt must name at least one right, or lev must carry at least one entry"
		});
		return;
	}
	if (instanceIndexes.length > 1) {
		ctx.addIssue({
			code: "custom",
			path: ["lev"],
			message: "a certificate carries at most one lev entry with a consumer"
		});
		return;
	}
	if (lev.length - instanceIndexes.length > 16) {
		ctx.addIssue({
			code: "custom",
			path: ["lev"],
			message: `a certificate carries at most 16 lev entries with no consumer`
		});
		return;
	}
	for (const index of instanceIndexes) {
		const entry = lev[index];
		if (instanceSlugOrNull(entry.slug, entry.id, entry.consumer.id) !== claims.appSlug) {
			ctx.addIssue({
				code: "custom",
				path: [
					"lev",
					index,
					"consumer"
				],
				message: "the instance slug of the lev entry does not equal appSlug"
			});
			return;
		}
	}
	if (canonicalByteLength({
		lrt: claims.lrt,
		lev: claims.lev
	}) > 16384) ctx.addIssue({
		code: "custom",
		path: [],
		message: `lrt and lev exceed ${RIGHTS_CERTIFICATE_LIBRARY_CLAIMS_MAX_BYTES} bytes`
	});
}
/**
* The zod form of {@link RightsCertificateClaims}. Strict at every level. `iss` is an `https:` URL
* of at most 256 printable ASCII characters with no `"` and no `\`. `sub` and `jti` are lower-case
* UUIDs. Each time is a non-negative safe integer. `krt` holds 0 to `KERNEL_RIGHTS.length` rights
* in canonical order, and is empty only when `lrt` is present or `lev` holds at least one entry: the
* instance entry of a library with no kernel right (R-026 item 3), or the consumer entries of an app
* whose only library claim is a library's events (R-041 item 2). `lrt`,
* when present, holds 1 to 64 library right names in code-point order. `lev`, when present, holds
* 1 to `LIBRARY_USES_MAX + 1` (17) entries in slug order: at most `LIBRARY_USES_MAX` (16) entries
* with no `consumer`, one per library use, plus the instance entry (R-034 item 6). At most one
* entry has a `consumer`, and for that entry
* `libraryInstanceSlug(slug, id, consumer.id)` must equal `appSlug` (R-033 lets it sit beside
* consumer entries and `lrt`). The canonical JSON of
* `{ lrt, lev }` is at most {@link RIGHTS_CERTIFICATE_LIBRARY_CLAIMS_MAX_BYTES} bytes.
* `csk.kid` matches {@link RIGHTS_CERTIFICATE_KID_PATTERN}, and `csk.jkt` holds 43 base64url
* characters. `images` and `uiFiles` follow the release statement rules.
*/
const rightsCertificateClaimsSchema = z.strictObject({
	...claimFields,
	aud: z.literal(RIGHTS_CERTIFICATE_AUDIENCE)
}).superRefine(checkLibraryClaims);
const claimsForVerifySchema = z.strictObject({
	...claimFields,
	aud: z.string().max(256)
}).superRefine(checkLibraryClaims);
/** One public ES256 P-256 key of a pin or a JWKS. `use: 'sig'` may appear; a private member fails. */
const rightsKeySchema = z.strictObject({
	kty: z.literal("EC"),
	crv: z.literal("P-256"),
	alg: z.literal("ES256"),
	x: z.string().max(43).regex(COORDINATE_PATTERN),
	y: z.string().max(43).regex(COORDINATE_PATTERN),
	kid: z.string().max(128).regex(RIGHTS_CERTIFICATE_KID_PATTERN),
	use: z.literal("sig").optional()
});
/** True when no two keys share a `kid`. */
function uniqueKids(keys) {
	return new Set(keys.map((key) => key.kid)).size === keys.length;
}
/**
* The zod form of {@link RightsCertificateTrustAnchor}. Strict. It admits 1 to 8 keys, each
* `kty: 'EC'`, `crv: 'P-256'`, `alg: 'ES256'` with 43-character `x` and `y`, a `kid` that matches
* {@link RIGHTS_CERTIFICATE_KID_PATTERN} and an optional `use: 'sig'`. It refuses a duplicate
* `kid`, a private key member and any other key type. A DP with an empty pin builds no verifier.
*/
const rightsCertificateTrustAnchorSchema = z.strictObject({
	issuer: issuerSchema,
	keys: z.array(rightsKeySchema).min(1).max(RIGHTS_KEYS_MAX).refine(uniqueKids, { message: "duplicate kid" })
});
/** The zod form of {@link RightsCertificateJwks}. Strict. The key rule of the anchor, with 0 to 8 keys. */
const rightsCertificateJwksSchema = z.strictObject({ keys: z.array(rightsKeySchema).max(RIGHTS_KEYS_MAX).refine(uniqueKids, { message: "duplicate kid" }) });
/** The refusals of {@link RightsCertificateVerifier.verify}, in check order. */
const RIGHTS_CERTIFICATE_REFUSALS = [
	"oversize",
	"malformed",
	"unsupported_alg",
	"wrong_typ",
	"unknown_kid",
	"bad_signature",
	"claims_invalid",
	"wrong_issuer",
	"wrong_audience",
	"not_yet_valid",
	"expired",
	"lifetime_exceeded",
	"app_mismatch"
];
const looseHeaderSchema = z.strictObject({
	alg: z.string(),
	typ: z.string(),
	kid: z.string()
});
/** The JSON value of base64url segment bytes, or `undefined` for bytes that are not UTF-8 JSON. */
function jsonOf(bytes) {
	try {
		return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
	} catch {
		return;
	}
}
/**
* The ten checks of {@link RightsCertificateVerifier.verify} over `anchor`, with `checkSignature`
* as step 6. Internal to `@zion/sdk-contracts`: the root barrel does not re-export it. The real
* verifier and `FakeRightsCertificateVerifier` both call it, so the two answer each refusal the
* same way.
*/
async function runRightsCertificateChecks(anchor, token, options, checkSignature) {
	const now = options.nowSeconds;
	if (typeof now !== "number" || !Number.isFinite(now)) throw new TypeError("RightsCertificateVerifier.verify: nowSeconds must be a finite number");
	if (typeof token !== "string") return {
		ok: false,
		reason: "malformed"
	};
	if (token.length > 65536) return {
		ok: false,
		reason: "oversize"
	};
	const segments = token.split(".");
	if (segments.length !== 3) return {
		ok: false,
		reason: "malformed"
	};
	const decoded = segments.map((segment) => segment.length === 0 ? null : base64UrlBytesOrNull(segment));
	if (decoded.includes(null)) return {
		ok: false,
		reason: "malformed"
	};
	const [headerText, payloadText] = segments;
	const [headerBytes, payloadBytes, signatureBytes] = decoded;
	const header = looseHeaderSchema.safeParse(jsonOf(headerBytes));
	if (!header.success) return {
		ok: false,
		reason: "malformed"
	};
	if (header.data.alg !== "ES256") return {
		ok: false,
		reason: "unsupported_alg"
	};
	if (header.data.typ !== "zion-rights-certificate+jwt") return {
		ok: false,
		reason: "wrong_typ"
	};
	const key = anchor.keys.find((candidate) => candidate.kid === header.data.kid);
	if (key === void 0) return {
		ok: false,
		reason: "unknown_kid"
	};
	if (signatureBytes.length !== 64) return {
		ok: false,
		reason: "bad_signature"
	};
	if (!await checkSignature(key, `${headerText}.${payloadText}`, signatureBytes, token)) return {
		ok: false,
		reason: "bad_signature"
	};
	const parsed = claimsForVerifySchema.safeParse(jsonOf(payloadBytes));
	if (!parsed.success) return {
		ok: false,
		reason: "claims_invalid"
	};
	const loose = parsed.data;
	if (loose.iss !== anchor.issuer) return {
		ok: false,
		reason: "wrong_issuer"
	};
	if (loose.aud !== "zion-dp-kernel") return {
		ok: false,
		reason: "wrong_audience"
	};
	const claims = {
		...loose,
		aud: RIGHTS_CERTIFICATE_AUDIENCE
	};
	const skew = 60;
	if (claims.nbf > now + skew || claims.iat > now + skew) return {
		ok: false,
		reason: "not_yet_valid"
	};
	if (claims.exp <= now - skew) return {
		ok: false,
		reason: "expired"
	};
	if (claims.exp - claims.iat > 259200) return {
		ok: false,
		reason: "lifetime_exceeded"
	};
	const expect = options.expect;
	if (expect !== void 0) {
		if (claims.appSlug !== expect.appSlug) return {
			ok: false,
			reason: "app_mismatch"
		};
		if (expect.version !== void 0 && claims.version !== expect.version) return {
			ok: false,
			reason: "app_mismatch"
		};
	}
	return {
		ok: true,
		kid: key.kid,
		claims
	};
}
/**
* The parsed anchor of `anchor`, or a `TypeError` that names the failed rule. Internal to
* `@zion/sdk-contracts`: the root barrel does not re-export it.
*/
function parseRightsCertificateAnchor(anchor) {
	const parsed = rightsCertificateTrustAnchorSchema.safeParse(anchor);
	if (!parsed.success) {
		const paths = parsed.error.issues.map((issue) => issue.path.map(String).join(".") || "(root)");
		throw new TypeError(`rights certificate trust anchor refused: ${paths.join(", ")}`);
	}
	return parsed.data;
}
/**
* The verifier of one trust anchor. It throws `TypeError` for an anchor that fails
* {@link rightsCertificateTrustAnchorSchema}, so a bad pin stops the process at boot. It imports
* each anchor key with WebCrypto at its first use; a key whose point WebCrypto refuses answers
* `bad_signature` for its tokens.
*/
function createRightsCertificateVerifier(anchor) {
	const pinned = parseRightsCertificateAnchor(anchor);
	const imported = /* @__PURE__ */ new Map();
	const keyFor = (key) => {
		let entry = imported.get(key.kid);
		if (entry === void 0) {
			entry = importEs256VerifyKey(key);
			imported.set(key.kid, entry);
		}
		return entry;
	};
	const checkSignature = async (key, signingInput, signature) => {
		const verifyKey = await keyFor(key);
		return verifyKey !== null && verifyEs256(verifyKey, signature, utf8ToBytes(signingInput));
	};
	return { verify: (token, options) => runRightsCertificateChecks(pinned, token, options, checkSignature) };
}
/**
* The JWS signing input of a certificate: the base64url of the canonical header JSON, a full stop
* and the base64url of the canonical claims JSON. WS-8b sends the SHA-256 of this text to KMS. It
* throws `RangeError` past 65,449 characters, the wire cap less a full stop and 86 signature
* characters, so the CP never signs a certificate that the wire cap or the verifier refuses. It
* throws `KernelEncodingError('not_canonical')` for a value that canonical JSON refuses. It does
* not check the schemas. Pure.
*/
function rightsCertificateSigningInput(header, claims) {
	const input = `${base64UrlEncode(utf8ToBytes(kernelCanonicalJson(header)))}.${base64UrlEncode(utf8ToBytes(kernelCanonicalJson(claims)))}`;
	if (input.length > SIGNING_INPUT_MAX_CHARS) throw new RangeError(`rightsCertificateSigningInput: ${input.length} characters exceed ${SIGNING_INPUT_MAX_CHARS}`);
	return input;
}
/**
* The compact certificate token: `signingInput`, a full stop and the base64url of `rawSignature`.
* `rawSignature` must be the 64-byte `r || s` form. Any other length throws `TypeError`, so a DER
* signature from KMS fails at the signer and never reaches a DP. Pure.
*/
function assembleRightsCertificate(signingInput, rawSignature) {
	if (rawSignature.length !== 64) throw new TypeError(`assembleRightsCertificate: a raw ES256 signature has 64 bytes, not ${rawSignature.length}`);
	return `${signingInput}.${base64UrlEncode(rawSignature)}`;
}
/**
* True when `digest` equals the `digest` or one of the `platformDigests` of an image in `claims`.
* False for text off the `sha256:` digest grammar, for example a tag such as `latest`. Pure.
*/
function rightsCertificateCoversImage(claims, digest) {
	if (!SHA256_DIGEST_PATTERN.test(digest)) return false;
	return claims.images.some((image) => image.digest === digest || image.platformDigests.some((d) => d === digest));
}
/**
* The SRI hash of the UI file at the exact `path`, or `null`. It changes no path, so `a/./b.js`
* finds nothing. Pure.
*/
function rightsCertificateUiFileHash(claims, path) {
	return claims.uiFiles.find((file) => file.path === path)?.sha384 ?? null;
}
//#endregion
export { KERNEL_ENROL_CERTIFICATES_MAX, RIGHTS_CERTIFICATE_AUDIENCE, RIGHTS_CERTIFICATE_CLOCK_SKEW_SECONDS, RIGHTS_CERTIFICATE_FILE_PATTERN, RIGHTS_CERTIFICATE_KID_PATTERN, RIGHTS_CERTIFICATE_LIBRARY_CLAIMS_MAX_BYTES, RIGHTS_CERTIFICATE_MAX_BYTES, RIGHTS_CERTIFICATE_MAX_LIFETIME_SECONDS, RIGHTS_CERTIFICATE_REFUSALS, RIGHTS_CERTIFICATE_TYP, assembleRightsCertificate, createRightsCertificateVerifier, parseRightsCertificateAnchor, rightsCertificateClaimsSchema, rightsCertificateCoversImage, rightsCertificateHeaderSchema, rightsCertificateJwksSchema, rightsCertificateSigningInput, rightsCertificateTrustAnchorSchema, rightsCertificateUiFileHash, runRightsCertificateChecks };

//# sourceMappingURL=rights-certificate.js.map